import React from "react";
import {
  Menu,
  MenuProps,
  Typography,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionMenuItem } from "../../primitives/MenuItem";
import {
  colorTokens,
  spacingTokens,
  radiusTokens,
  shadowTokens,
} from "../../tokens";

export interface QuickActionItem {
  /** Unique action identifier */
  id: string;
  /** Display label */
  label: string;
  /** Icon component */
  icon: React.ReactNode;
  /** Optional tooltip */
  tooltip?: string;
  /** Whether action is disabled */
  disabled?: boolean;
  /** Disabled reason (shown in tooltip) */
  disabledReason?: string;
  /** Action group */
  group: string;
}

export interface QuickActionMenuProps extends Omit<MenuProps, "onClose"> {
  /** Menu anchor element */
  anchorEl: HTMLElement | null;
  /** Whether menu is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Actions to display */
  actions: QuickActionItem[];
  /** Group labels */
  groupLabels?: Record<string, string>;
  /** Handler when action is clicked */
  onActionClick: (action: QuickActionItem) => void;
  /** Whether any actions are loading */
  loading?: boolean;
  /** RTL mode */
  isRTL?: boolean;
}

/**
 * QuickActionMenu Component
 *
 * Dropdown menu for displaying grouped quick actions
 * with icons, tooltips, and disabled states
 */
export const QuickActionMenu: React.FC<QuickActionMenuProps> = ({
  anchorEl,
  open,
  onClose,
  actions,
  groupLabels = {},
  onActionClick,
  loading = false,
  isRTL = false,
  ...menuProps
}) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const effectiveRTL = isRTL || dir === "rtl";
  // Group actions by category
  const groupedActions = actions.reduce((acc, action) => {
    if (!acc[action.group]) {
      acc[action.group] = [];
    }
    acc[action.group].push(action);
    return acc;
  }, {} as Record<string, QuickActionItem[]>);

  const groups = Object.keys(groupedActions);

  const handleActionClick = (action: QuickActionItem) => {
    if (loading) return;
    // Always forward the click — parent shows a toast when the action is blocked
    // (e.g. customer not linked). Visual disabled styling still applies below.
    onActionClick(action);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: effectiveRTL ? "left" : "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: effectiveRTL ? "left" : "right",
      }}
      MenuListProps={{
        dense: true,
        sx: { py: 0.5 },
      }}
      PaperProps={{
        sx: {
          mt: 0.5,
          minWidth: 200,
          maxWidth: 280,
          borderRadius: radiusTokens.md,
          boxShadow: shadowTokens.md,
          border: `1px solid ${colorTokens.semantic.border.default}`,
          direction: dir,
        },
      }}
      {...menuProps}
    >
      {groups.length === 0 && (
        <Box sx={{ px: 1.5, py: 1.5, textAlign: "center" }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {t("frontdesk.detailPanel.noActionsAvailable")}
          </Typography>
        </Box>
      )}

      {groups.map((group, groupIndex) => (
        <Box key={group}>
          {/* Group Label */}
          {groupLabels[group] && (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                backgroundColor: colorTokens.neutral[50],
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colorTokens.semantic.text.secondary,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: 9,
                  letterSpacing: "0.4px",
                  lineHeight: 1.2,
                }}
              >
                {groupLabels[group]}
              </Typography>
            </Box>
          )}

          {/* Group Actions */}
          {groupedActions[group].map((action) => {
            const isDisabled = action.disabled || loading;
            const tooltipContent = action.disabled
              ? action.disabledReason || action.tooltip
              : action.tooltip;

            const menuItem = (
              <ActionMenuItem
                key={action.id}
                id={action.id}
                label={action.label}
                icon={action.icon}
                // Keep look disabled, but allow click so parent can explain why
                disabled={false}
                onClick={() => handleActionClick(action)}
                sx={{
                  px: 1.25,
                  py: 0.4,
                  mx: 0.25,
                  gap: 1,
                  minHeight: 32,
                  "& .MuiTypography-root": {
                    fontSize: 12.5,
                    lineHeight: 1.25,
                  },
                  ...(isDisabled
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : {}),
                }}
              />
            );

            // Wrap in tooltip if there's a tooltip or disabled reason
            return tooltipContent ? (
              <Tooltip
                key={action.id}
                title={tooltipContent}
                placement={effectiveRTL ? "right" : "left"}
                arrow
              >
                <span>{menuItem}</span>
              </Tooltip>
            ) : (
              menuItem
            );
          })}

          {/* Divider between groups */}
          {groupIndex < groups.length - 1 && <Divider sx={{ my: 0.25 }} />}
        </Box>
      ))}
    </Menu>
  );
};
